export interface ResumePoint {
  seconds: number;
  updatedAt: number;
}

export function parseTimestampParam(raw: string | null | undefined): number | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed || !/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.floor(value);
}

export function clampTimestamp(seconds: number, durationSeconds: number): number {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const duration = Number(durationSeconds);
  const max = Number.isFinite(duration) && duration > 0 ? Math.floor(duration - 0.05) : safe;
  return Math.max(0, Math.min(safe, Math.max(0, max)));
}

export function formatShareTime(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    : `${minutes}:${String(remainder).padStart(2, "0")}`;
}

export function buildMomentUrl(origin: string, slug: string, seconds: number): string {
  const cleanOrigin = origin.replace(/\/+$/, "");
  return `${cleanOrigin}/v/${encodeURIComponent(slug)}?t=${Math.max(0, Math.floor(seconds))}`;
}

export function buildStableUrl(origin: string, slug: string): string {
  const cleanOrigin = origin.replace(/\/+$/, "");
  return `${cleanOrigin}/v/${encodeURIComponent(slug)}`;
}

export function averageWatched(watchedSeconds: number, plays: number): number | null {
  if (!Number.isFinite(watchedSeconds) || !Number.isFinite(plays) || plays <= 0) return null;
  return Math.round(watchedSeconds / plays);
}

export function momentUrlFromLocation(seconds: number): string {
  const params = new URLSearchParams(window.location.search);
  params.set("t", String(Math.max(0, Math.floor(seconds))));
  return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
}

export function updateMomentInAddressBar(seconds: number): void {
  try {
    window.history.replaceState(null, "", momentUrlFromLocation(seconds));
  } catch {
    // History writes can fail in embedded contexts; sharing still works via the copy button.
  }
}

const RESUME_PREFIX = "work-index:resume:";
const RESUME_MIN_SECONDS = 15;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readResumePoint(videoId: string): ResumePoint | null {
  try {
    const raw = storage()?.getItem(RESUME_PREFIX + videoId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ResumePoint>;
    const seconds = Number(parsed.seconds);
    const updatedAt = Number(parsed.updatedAt);
    if (!Number.isFinite(seconds) || seconds < RESUME_MIN_SECONDS) return null;
    if (!Number.isFinite(updatedAt)) return null;
    return { seconds: Math.floor(seconds), updatedAt };
  } catch {
    return null;
  }
}

export function saveResumePoint(videoId: string, seconds: number): void {
  try {
    if (seconds < 1) return;
    storage()?.setItem(RESUME_PREFIX + videoId, JSON.stringify({ seconds: Math.floor(seconds), updatedAt: Date.now() }));
  } catch {
    // Storage may be unavailable in private modes; resume is best-effort.
  }
}

export function clearResumePoint(videoId: string): void {
  try {
    storage()?.removeItem(RESUME_PREFIX + videoId);
  } catch {
    // Nothing to do.
  }
}

export function resumeWantsOffer(point: ResumePoint, durationSeconds: number): boolean {
  const duration = Number(durationSeconds);
  if (!Number.isFinite(duration) || duration <= 0) return false;
  return point.seconds >= RESUME_MIN_SECONDS && point.seconds < duration * 0.95;
}

export function completionRate(completions: number, plays: number): string | null {
  if (!Number.isFinite(completions) || !Number.isFinite(plays) || plays <= 0) return null;
  return `${Math.min(100, Math.round((completions / plays) * 100))}%`;
}
